import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Chip,
  IconButton,
  ListItem,
  ListItemButton,
  Stack
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  LocationOn as LocationIcon,
  Person as PersonIcon,
  FitnessCenter as ClassIcon,
  Sports as CoachingIcon,
  Games as AmenityIcon,
  Groups as GroupPlayIcon,
  ChevronRight as ChevronRightIcon
} from '@mui/icons-material';
import { DashboardBooking } from '../../types/dashboard';
import { formatTimeString, isToday, parseDateOnlyString, isDateOnly, formatDate, formatDateForInput } from '../../utils/dateUtils';
import { useNavigate } from 'react-router-dom';

interface BookingCardProps {
  booking: DashboardBooking;
  isCompact?: boolean;
}

const BookingCard: React.FC<BookingCardProps> = ({ booking, isCompact = false }) => {
  const navigate = useNavigate();

  const capitalizeStatus = (status: string) => {
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
  };

  const getBookingIcon = (type: string) => {
    switch (type) {
      case 'class':
        return <ClassIcon fontSize="small" />;
      case 'coaching-session':
      case 'coaching-program':
        return <CoachingIcon fontSize="small" />;
      case 'amenity':
        return <AmenityIcon fontSize="small" />;
      case 'group-play':
        return <GroupPlayIcon fontSize="small" />;
      default:
        return <ClassIcon fontSize="small" />;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      case 'completed':
        return 'info';
      case 'waitlist':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getTypeColor = (type: string) => {
    const typeColorMap: Record<string, string> = {
      class: '#1976d2',
      'coaching-session': '#8e24aa',
      'coaching-program': '#2e7d32',
      amenity: '#f57c00',
      'group-play': '#d32f2f',
      cardio: '#1976d2',
      squash: '#e91e63',
      tennis: '#4caf50',
      pickleball: '#ff9800'
    };
    return typeColorMap[type] || '#6c757d';
  };

  // Helper to format time display: remove leading zero and remove space before AM/PM
  const formatTimeDisplay = (timeStr: string) => {
    if (!timeStr) return '';
    const formatted = formatTimeString(timeStr);
    // Remove leading zero like 04: -> 4:
    const noLeadingZero = formatted.replace(/^0(?=\d:)/, '');
    // Remove space before AM/PM and uppercase
    return noLeadingZero.replace(/\s+/g, '').toUpperCase();
  };

  // Compute duration between start and end time strings
  const computeDurationDisplay = (start?: string, end?: string) => {
    if (!start || !end) return '';
    try {
      // strip timezone suffix
      const strip = (s: string) => s.split(/\+|-/)[0];
      const [sh, sm] = strip(start).split(':').map(Number);
      const [eh, em] = strip(end).split(':').map(Number);
      const now = new Date();
      const sd = new Date(now.getFullYear(), now.getMonth(), now.getDate(), sh || 0, sm || 0, 0);
      const ed = new Date(now.getFullYear(), now.getMonth(), now.getDate(), eh || 0, em || 0, 0);
      let diff = (ed.getTime() - sd.getTime()) / 60000; // minutes
      if (diff <= 0) diff = Math.abs(diff); // fallback
      if (diff >= 60) {
        const h = Math.floor(diff / 60);
        const m = Math.round(diff % 60);
        return m === 0 ? `${h}h` : `${h}h ${m}m`;
      }
      return `${Math.round(diff)}m`;
    } catch (err) {
      return '';
    }
  };

  const handleClick = () => {
    // Navigate to appropriate detail page based on booking type
    switch (booking.type) {
      case 'class':
        // For class bookings, navigate to the class instance booking page
        // Use classInstanceId from the booking data (not the booking ID itself)
        const instanceId = booking.rawData.classInstanceId;
        if (instanceId) {
          navigate(`/member/class-booking/${instanceId}`, { state: { from: 'dashboard' } });
        } else {
          // Fallback to class detail page if no instance ID
          const classId = booking.rawData.classId || booking.rawData.parentClassId;
          if (classId) {
            navigate(`/classes/${classId}`, { state: { from: 'dashboard' } });
          } else {
            console.warn('No valid class or instance ID found for booking:', booking);
          }
        }
        break;
      case 'coaching-session':
        const sessionId = booking.rawData.coachingSessionId || booking.rawData.id;
        navigate(`/coaching/sessions/${sessionId}`);
        break;
      case 'coaching-program':
        // Check if this is a program instance or program enrollment
        if (booking.rawData.programId) {
          // This is a program instance - navigate to the program detail page
          const programId = booking.rawData.programId;
          navigate(`/coaching/programs/${programId}`);
        } else {
          // This is a program enrollment - navigate to the program detail page
          const enrollmentProgramId = booking.rawData.coachingProgramId || booking.rawData.parentProgramId || booking.rawData.id;
          navigate(`/coaching/programs/${enrollmentProgramId}`);
        }
        break;
      case 'amenity':
        // Navigate to member amenity booking detail page
        const bookingId = booking.rawData.id;
        if (bookingId) {
          navigate(`/member/amenity-booking/${bookingId}`, { state: { from: 'dashboard' } });
        } else {
          console.warn('No valid booking ID found for amenity booking:', booking);
        }
        break;
      case 'group-play':
        // Navigate to group play booking page
        const groupPlayInstanceId = booking.rawData.groupPlayInstanceId;
        const groupPlayId = booking.rawData.instance?.parentGroupPlayId || booking.rawData.groupPlayId;
        if (groupPlayId && groupPlayInstanceId) {
          navigate(`/member/group-play/${groupPlayId}/instance/${groupPlayInstanceId}`, { state: { from: 'dashboard' } });
        } else {
          console.warn('No valid group play IDs found for booking:', booking);
        }
        break;
      default:
        console.log('View booking details:', booking);
        // You could show a toast or modal with booking details as fallback
    }
  };

  if (isCompact) {
    // Format the date for display
    const bookingDate = isDateOnly(booking.date) 
      ? parseDateOnlyString(booking.date)
      : new Date(booking.date);
    
    const today = new Date();
    const isBookingToday = formatDateForInput(bookingDate) === formatDateForInput(today);
    const isTomorrow = formatDateForInput(bookingDate) === formatDateForInput(new Date(Date.now() + 86400000));
    const dateLabel = isBookingToday ? 'Today' : isTomorrow ? 'Tomorrow' : formatDate(booking.date);
    
    const startTime = booking.startTime || '';
    const endTime = booking.endTime || '';
    const duration = computeDurationDisplay(startTime, endTime);
    const barColor = getTypeColor(booking.type);

    return (
      <ListItem divider sx={{ alignItems: 'stretch', p: 0 }}>
        <ListItemButton onClick={handleClick} sx={{ width: '100%', alignItems: 'stretch', p: 2 }}>
          <Box sx={{ display: 'flex', width: '100%' }}>
            {/* left time column */}
            <Box sx={{ width: 88, display: 'flex', flexDirection: 'column', alignItems: 'center', pr: 1 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'left', pl: 2 }}>
                {dateLabel && <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', mb: 0.25 }}>{dateLabel}</Typography>}
                <Typography variant="subtitle2">{formatTimeDisplay(startTime)}</Typography>
                {duration ? <Typography variant="subtitle2" color="text.secondary" fontWeight={600}>{duration}</Typography> : null}
              </Box>
            </Box>

            {/* colored separator bar between time and details */}
            <Box sx={{ width: 8, backgroundColor: barColor, borderRadius: 1, mx: 1, alignSelf: 'stretch' }} />

            {/* details */}
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography 
                    variant="subtitle1" 
                    sx={{ 
                      fontWeight: 600,
                      fontSize: { xs: '0.9rem', sm: '1rem' } // Smaller on mobile
                    }}
                  >
                    {booking.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {/* For amenity bookings, show host name instead of venue */}
                    {booking.type === 'amenity' && booking.hostName 
                      ? `Host: ${booking.hostName}` 
                      : (booking.instructor || booking.coachName) 
                        ? `${booking.instructor || booking.coachName}` 
                        : ''}
                    {/* Only show venue for non-amenity bookings */}
                    {booking.type !== 'amenity' && booking.venue ? ` • ${booking.venue}` : ''}
                  </Typography>
                  <Box sx={{ mt: 0.5 }}>
                    <Chip
                      size="small"
                      label={capitalizeStatus(booking.status)}
                      color={getStatusColor(booking.status) as any}
                      sx={{ mr: 1 }}
                    />
                  </Box>
                </Box>
                <Box sx={{ textAlign: 'right', display: { xs: 'none', sm: 'block' } }}>
                  {booking.type === 'amenity' ? (
                    booking.memberAmountPaid !== null && booking.memberAmountPaid !== undefined ? (
                      <Typography variant="caption" display="block">${Number(booking.memberAmountPaid).toFixed(2)}</Typography>
                    ) : null
                  ) : booking.rawData?.paidAmount !== null && booking.rawData?.paidAmount !== undefined ? (
                    parseFloat(booking.rawData.paidAmount) === 0 ? (
                      <Typography variant="caption" display="block" color="success.main">Free</Typography>
                    ) : (
                      <Typography variant="caption" display="block">${Number(booking.rawData.paidAmount).toFixed(2)}</Typography>
                    )
                  ) : booking.cost ? (
                    <Typography variant="caption" display="block">${Number(booking.cost).toFixed(2)}</Typography>
                  ) : null}
                </Box>
              </Stack>
            </Box>
          </Box>
        </ListItemButton>
      </ListItem>
    );
  }

  return (
    <Card
      onClick={handleClick}
      sx={{
        cursor: 'pointer',
        '&:hover': {
          elevation: 3,
          bgcolor: 'action.hover'
        },
        mb: 1
      }}
    >
      <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ color: 'primary.main', mt: 0.5 }}>
            {getBookingIcon(booking.type)}
          </Box>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" gutterBottom>
              {booking.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <TimeIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {(() => {
                    const bookingDate = isDateOnly(booking.date) 
                      ? parseDateOnlyString(booking.date)
                      : new Date(booking.date);
                    const isTodaysBooking = isToday(bookingDate);
                    
                    return isTodaysBooking ? (
                      `${formatTimeString(booking.startTime)} - ${formatTimeString(booking.endTime)}`
                    ) : (
                      `${bookingDate.toLocaleDateString('en-US', { 
                        weekday: 'short', 
                        month: 'short', 
                        day: 'numeric' 
                      })} • ${formatTimeString(booking.startTime)} - ${formatTimeString(booking.endTime)}`
                    );
                  })()}
                </Typography>
              </Box>
              {booking.venue && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <LocationIcon fontSize="small" color="action" />
                  <Typography variant="body2" color="text.secondary">
                    {booking.venue}
                  </Typography>
                </Box>
              )}
            </Box>
            {(booking.instructor || booking.coachName) && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                <PersonIcon fontSize="small" color="action" />
                <Typography variant="body2" color="text.secondary">
                  {booking.instructor || booking.coachName}
                </Typography>
              </Box>
            )}
            {booking.type === 'amenity' ? (
              booking.memberAmountPaid !== null && booking.memberAmountPaid !== undefined ? (
                <Typography variant="body2" color="text.secondary">
                  Cost: ${Number(booking.memberAmountPaid).toFixed(2)}
                </Typography>
              ) : null
            ) : booking.rawData?.paidAmount !== null && booking.rawData?.paidAmount !== undefined ? (
              parseFloat(booking.rawData.paidAmount) === 0 ? (
                <Typography variant="body2" color="success.main">Free</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Cost: ${Number(booking.rawData.paidAmount).toFixed(2)}
                </Typography>
              )
            ) : booking.cost ? (
              <Typography variant="body2" color="text.secondary">
                Cost: ${Number(booking.cost).toFixed(2)}
              </Typography>
            ) : null}
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
            <Chip
              size="small"
              label={capitalizeStatus(booking.status)}
              color={getStatusColor(booking.status) as any}
            />
            <IconButton size="small" sx={{ p: 0.5 }}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default BookingCard;